import { useSyncExternalStore } from 'react';

/**
 * Per-brand Site Settings, mirrored from the super-admin panel.
 *
 * The server resolves the brand from Host and returns these already merged over
 * the defaults on `GET /brand` (`settings`), so nothing here has to guess what a
 * missing key means. The shape must stay in step with the backend's
 * `BRAND_SETTINGS` and the super-admin's `SiteSettings`.
 */
export interface SiteSettings {
  /** OFF = brand not launched yet; players see the "coming soon" screen. */
  siteLive: boolean;
  /** ON = brand is down for work; players see the maintenance screen. */
  maintenance: boolean;
  /** Show Sign Up / Register anywhere in the UI. */
  signup: boolean;
  /** Offer the "Login with demo ID" button. */
  demoLogin: boolean;
  /** Show the Deposit / Withdraw / FD cashier buttons. */
  deposit: boolean;
  /** Let players reach the login screen at all. */
  showLogin: boolean;
  /** Show fancy markets on football and tennis matches. */
  showFancies: boolean;
  /** New provider matches appear without an admin switching them on. */
  autoSports: boolean;
  /** A player who has never changed their password must do so before playing. */
  forceChangePassword: boolean;
}

/** Resting state — matches the backend's DEFAULT_BRAND_SETTINGS exactly. */
export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  siteLive: true,
  maintenance: false,
  signup: true,
  demoLogin: true,
  deposit: true,
  showLogin: true,
  showFancies: true,
  autoSports: true,
  forceChangePassword: false,
};

/**
 * `loading` until GET /brand answers. The app holds a splash during it rather
 * than painting, so a brand in maintenance never flashes its real site first.
 */
export type BrandStatus = 'loading' | 'ready';

/* --- store ---------------------------------------------------------------- */
// useSyncExternalStore compares snapshots by identity, so the snapshot object is
// rebuilt only when something actually changes — returning a fresh object every
// read would spin React forever.
let snapshot: { status: BrandStatus; settings: SiteSettings } = {
  status: 'loading',
  settings: DEFAULT_SITE_SETTINGS,
};
const subs = new Set<() => void>();

function subscribe(cb: () => void) {
  subs.add(cb);
  return () => {
    subs.delete(cb);
  };
}

function emit() {
  subs.forEach((f) => f());
}

/** Merge whatever the server sent over the defaults; ignore junk keys. */
export function setSiteSettings(raw: unknown): void {
  const next = { ...DEFAULT_SITE_SETTINGS };
  if (raw && typeof raw === 'object') {
    const src = raw as Record<string, unknown>;
    (Object.keys(DEFAULT_SITE_SETTINGS) as (keyof SiteSettings)[]).forEach((k) => {
      if (typeof src[k] === 'boolean') next[k] = src[k] as boolean;
    });
  }
  snapshot = { status: snapshot.status, settings: next };
  emit();
}

/**
 * Called once the brand fetch settles — including when it FAILS. A backend the
 * app cannot reach must not leave players staring at a splash forever, so a
 * failed lookup falls through to the defaults and the site renders as normal.
 */
export function markBrandResolved(): void {
  if (snapshot.status === 'ready') return;
  snapshot = { status: 'ready', settings: snapshot.settings };
  emit();
}

/* --- hooks ---------------------------------------------------------------- */
export function useSiteSettings(): SiteSettings {
  return useSyncExternalStore(subscribe, () => snapshot.settings);
}

export function useBrandStatus(): BrandStatus {
  return useSyncExternalStore(subscribe, () => snapshot.status);
}

/** Non-reactive read, for code outside the React tree (route guards, RTK). */
export function getSiteSettings(): SiteSettings {
  return snapshot.settings;
}
